import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn, isNumeric } from '../../lib/utils';
import { badgeVariants } from './variants';
import type { BadgeProps } from './types';

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      asChild = false,
      children,
      badgeContent,
      variant = 'primary',
      anchorOrigin = { vertical: 'top', horizontal: 'right' },
      offsetX = 0,
      offsetY = 0,
      className,
      badgeClassName,
      badgeProps,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'span';

    const hasContent =
      badgeContent !== null &&
      badgeContent !== undefined &&
      (isNumeric(badgeContent) ? badgeContent > 0 : true);

    let colorClasses = '';
    const defaults = cn(
      'eye:text-black eye:border eye:min-w-[20px] eye:overflow-hidden',
      'eye:text-[0.75rem] eye:font-medium',
      colorClasses,
    );

    if (!React.isValidElement(badgeContent)) {
      const variants = badgeVariants;

      if (variant in variants) {
        colorClasses = cn(defaults, variants[variant]);
      }
    }

    // Vertical placement
    const verticalPlacement =
      anchorOrigin.vertical === 'top'
        ? 'eye:top-0 eye:-translate-y-1/2'
        : 'eye:bottom-0 eye:translate-y-1/2';

    // Compute inline styles for offsets
    const horizontalOffset =
      anchorOrigin.horizontal === 'right'
        ? { left: `calc(100% + ${offsetX}px)` }
        : { right: `calc(100% + ${offsetX}px)` };

    const verticalOffset =
      anchorOrigin.vertical === 'top' ? { top: offsetY } : { bottom: offsetY };
    const styleOffset: React.CSSProperties = {
      ...horizontalOffset,
      ...verticalOffset,
    };

    return (
      <span ref={ref} className={cn('eye:relative', className)} {...props}>
        <Comp data-slot='badge-child'>{children}</Comp>

        {hasContent && (
          <span
            {...badgeProps}
            className={cn(
              'eye:absolute eye:flex eye:h-[20px] eye:px-[4px]',
              'eye:items-center eye:justify-center eye:rounded-full eye:whitespace-nowrap',
              verticalPlacement,
              colorClasses,
              badgeClassName,
            )}
            style={styleOffset}
          >
            {badgeContent}
          </span>
        )}
      </span>
    );
  },
);

Badge.displayName = 'Badge';
